import {
  BatteryAlert,
  DirectionsCar,
  Engineering,
  Error as ErrorIcon,
  Info as InfoIcon,
  LocalGasStation,
  ReportProblem,
  WarningAmber,
} from "@mui/icons-material";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import React from "react";
import { VehicleFaultEventDto } from "../../../api/vehiclesApi";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";

interface FaultsTableProps {
  faults: VehicleFaultEventDto[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  
  // Search functionality
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const FaultsTable: React.FC<FaultsTableProps> = ({
  faults,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
  
  // Search functionality
  enableSearch = false,
  onSearchChange,
}) => {
  const columns: Column[] = [
    {
      field: "time",
      headerName: "Time",
      flex: 1,
      renderCell: (_: any, fault: VehicleFaultEventDto) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {format(new Date(fault.firstSeenAt), "MM/dd/yyyy HH:mm", {
              locale: enUS,
            })}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Last seen:{" "}
            {format(new Date(fault.lastSeenAt), "MM/dd/yyyy HH:mm", {
              locale: enUS,
            })}
          </Typography>
        </Box>
      ),
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 120,
      renderCell: (value: string) => {
        const getSeverityStyle = () => {
          switch (value.toLowerCase()) {
            case "critical":
              return {
                icon: <ErrorIcon />,
                style: {
                  backgroundColor: "error.light",
                  color: "error.dark",
                  borderColor: "error.main",
                  border: 1,
                  "& .MuiSvgIcon-root": {
                    color: "error.dark",
                  },
                },
              };
            case "warning":
              return {
                icon: <WarningAmber />,
                style: {
                  backgroundColor: "warning.light",
                  color: "warning.dark",
                  borderColor: "warning.main",
                  border: 1,
                  "& .MuiSvgIcon-root": {
                    color: "warning.dark",
                  },
                },
              };
            default:
              return {
                icon: <InfoIcon />,
                style: {
                  backgroundColor: "info.light",
                  color: "info.dark",
                  borderColor: "info.main",
                  border: 1,
                  "& .MuiSvgIcon-root": {
                    color: "info.dark",
                  },
                },
              };
          }
        };

        const style = getSeverityStyle();

        return (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Chip
              icon={style.icon}
              label={value}
              size="small"
              sx={{
                ...style.style,
                width: 100,
                height: "24px",
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.75rem",
                  width: "100%",
                  textAlign: "center",
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "warningLights",
      headerName: "Warning Lights",
      width: 200,
      renderCell: (_: any, fault: VehicleFaultEventDto) => {
        const getLightIcon = (type: string) => {
          switch (type) {
            case "Stop Engine":
              return {
                icon: <BatteryAlert />,
                tooltip: "Stop Engine Light is ON",
                style: {
                  color: "error.main",
                },
              };
            case "Protect":
              return {
                icon: <ErrorIcon />,
                tooltip: "Protect Engine Light is ON",
                style: {
                  color: "error.main",
                },
              };
            case "Warning":
              return {
                icon: <WarningAmber />,
                tooltip: "Warning Light is ON",
                style: {
                  color: "warning.main",
                },
              };
            case "Emissions":
              return {
                icon: <LocalGasStation />,
                tooltip: "Emissions Light is ON",
                style: {
                  color: "warning.main",
                },
              };
            case "Check Engine":
              return {
                icon: <Engineering />,
                tooltip: "Check Engine Light is ON",
                style: {
                  color: "warning.main",
                },
              };
            default:
              return {
                icon: <ReportProblem />,
                tooltip: "Unknown Warning Light",
                style: {
                  color: "info.main",
                },
              };
          }
        };

        const activeLights = [];
        if (fault.checkEngineLightOn) activeLights.push("Check Engine");
        if (fault.stopLightOn) activeLights.push("Stop Engine");
        if (fault.warningLightOn) activeLights.push("Warning");
        if (fault.protectLightOn) activeLights.push("Protect");
        if (fault.emissionsLightOn) activeLights.push("Emissions");

        if (activeLights.length === 0) return "-";

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {activeLights.map((light) => {
              const { icon, tooltip, style } = getLightIcon(light);
              return (
                <Tooltip key={light} title={tooltip} arrow placement="top">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.8,
                      },
                      ...style,
                    }}
                  >
                    {icon}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "spn",
      headerName: "SPN",
      flex: 1,
      renderCell: (_: any, fault: VehicleFaultEventDto) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {fault.spnId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fault.spnDescription}
          </Typography>
        </Box>
      ),
    },
    {
      field: "fmi",
      headerName: "FMI",
      flex: 1,
      renderCell: (_: any, fault: VehicleFaultEventDto) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {fault.fmiId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fault.fmiDescription}
          </Typography>
        </Box>
      ),
    },
    {
      field: "dtc",
      headerName: "DTC",
      flex: 1,
      renderCell: (_: any, fault: VehicleFaultEventDto) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {fault.dtcCode || "-"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fault.dtcDescription || "-"}
          </Typography>
        </Box>
      ),
    },
  ];

  // Define searchable columns for vehicle faults
  const searchableColumns = makeColumnsSearchable(
    columns,
    ["severity", "dtc"],
    {
      severity: "string",
      dtc: "string",
    }
  );

  const renderVehicleGroupHeader = (
    _groupKey: string,
    groupItems: VehicleFaultEventDto[]
  ) => {
    const firstFault = groupItems[0];
    if (!firstFault) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DirectionsCar fontSize="small" color="primary" />
          <Chip
            label="Vehicle"
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.75rem", fontWeight: 600 }}
          />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {firstFault.vehicleName || `Vehicle ${firstFault.vehicleId}`}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {groupItems.length} fault{groupItems.length !== 1 ? "s" : ""}
        </Typography>
      </Box>
    );
  };

  return (
    <DataTable
      columns={columns}
      items={faults}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
      
      // Search functionality
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
      getRowId={(fault) => fault.id}
      grouping={{
        getGroupKey: (fault) => String(fault.vehicleId),
        renderGroupHeader: renderVehicleGroupHeader,
        collapsedItemLimit: 5,
        itemLabel: "fault",
        countMode: "items",
      }}
    />
  );
};

export default FaultsTable;
